import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { resetArrangement, assertWarnungen } from "./utils";
import { ZElement } from "../arrangement/ZElement";
import { Arrangement } from "../arrangement/Arrangement";

/**
 * Testet die statische (NICHT-animierte) Spiegelung des Schiebetores in Abhängigkeit von
 * Schieberichtung (links/rechts) und Motorseite (A/B) sowie die Existenz der konstruktiv immer
 * vorhandenen 3D-Teile (Fundamente, Leerrohr).
 *
 * Bewusst NICHT getestet: die Öffnungs-Animation (schiebetorTranslationsPunkt-Verschiebung),
 * da Animationen laut Vorgabe ausgeklammert sind. Geprüft werden ausschließlich die nach
 * `update3d()` stabilen geometrischen Zustände.
 */
export default {
  name: "TestSchiebetorSpiegelung",
};

async function createSchiebetor(
  schieberichtung: string,
  motorseite: string
): Promise<{ agt: Arrangement; tor: ZElement }> {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Strecke", [10]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Schiebetor_Easy"),
  });
  agt.update3d();
  const tor = agt.getTore()[0];
  tor.schieberichtungAuswahlwert = APP.richMetadata.findAuswahlwert(schieberichtung);
  tor.schiebetormotorseiteAuswahlwert = APP.richMetadata.findAuswahlwert(motorseite);
  agt.update3d();
  return { agt, tor };
}

// ---------------------------------------------------------------------------
// 1) isAnschlagLinks-Wahrheitstabelle (a ? l : !l)
//    a = Motorseite A, l = Schieberichtung links
// ---------------------------------------------------------------------------
export const testAnschlagLinksWahrheitstabelle: Test = async () => {
  const fälle: Array<[string, string, boolean]> = [
    ["Schieberichtung_links", "Schiebetormotorseite_A", true],
    ["Schieberichtung_rechts", "Schiebetormotorseite_A", false],
    ["Schieberichtung_links", "Schiebetormotorseite_B", false],
    ["Schieberichtung_rechts", "Schiebetormotorseite_B", true],
  ];
  for (const [schieberichtung, motorseite, erwartet] of fälle) {
    await subTestContext(`${schieberichtung}/${motorseite}`, async () => {
      const { tor } = await createSchiebetor(schieberichtung, motorseite);
      assert.equals(erwartet, tor.isAnschlagLinks, "isAnschlagLinks");
    });
  }
};

// ---------------------------------------------------------------------------
// 2) schiebetorZOffset kippt mit der Motorseite (±0.09)
// ---------------------------------------------------------------------------
export const testZOffsetKipptMitMotorseite: Test = async () => {
  const a = await createSchiebetor("Schieberichtung_rechts", "Schiebetormotorseite_A");
  assert.equals(-0.09, a.tor.schiebetorZOffset, "Motorseite A -> -0.09");
  const b = await createSchiebetor("Schieberichtung_rechts", "Schiebetormotorseite_B");
  assert.equals(0.09, b.tor.schiebetorZOffset, "Motorseite B -> +0.09");
};

// ---------------------------------------------------------------------------
// 3) Der FixPunkt spiegelt Position und Rotation gemäß isAnschlagLinks
// ---------------------------------------------------------------------------
export const testFixPunktSpiegelung: Test = async () => {
  const fälle: Array<[string, string]> = [
    ["Schieberichtung_links", "Schiebetormotorseite_A"], // isAnschlagLinks = true
    ["Schieberichtung_rechts", "Schiebetormotorseite_A"], // isAnschlagLinks = false
  ];
  for (const [schieberichtung, motorseite] of fälle) {
    await subTestContext(`${schieberichtung}/${motorseite}`, async () => {
      const { tor } = await createSchiebetor(schieberichtung, motorseite);
      const fix = tor.schiebetorFixPunkt;
      // x: 0 bei Anschlag links, sonst um die Torbreite verschoben
      assert.equals(tor.isAnschlagLinks ? 0 : tor.breite, fix.position.x, "FixPunkt.position.x");
      // z: entspricht dem Z-Offset
      assert.equals(tor.schiebetorZOffset, fix.position.z, "FixPunkt.position.z == schiebetorZOffset");
      // Rotation: Identität (w≈1) bei Anschlag links, 180°-um-Y (w≈0) sonst
      assert.equals(tor.isAnschlagLinks ? 1 : 0, Math.abs(fix.quaternion.w), "FixPunkt-Rotation", 0.001);
    });
  }
};

// ---------------------------------------------------------------------------
// 4) Konstruktiv immer vorhandene 3D-Teile existieren (Fundamente + Leerrohr)
// ---------------------------------------------------------------------------
export const testLaufwerkteileExistieren: Test = async () => {
  const { agt, tor } = await createSchiebetor("Schieberichtung_rechts", "Schiebetormotorseite_A");
  assertWarnungen(agt, []);
  const p = tor.schiebetorFixPunkt as any;
  for (const key of ["Schiebetor_Fundament1", "Schiebetor_Fundament2", "Schiebetor_Fundament3"]) {
    assert.isNotNull(p[key], `${key} muss existieren`);
    assert.isTrue(Array.isArray(p[key].points) && p[key].points.length > 0, `${key} hat Punkte`);
  }
  assert.isNotNull(p["Leerrohr"], "Leerrohr muss existieren");
  assert.isTrue(Array.isArray(p["Leerrohr"].paths) && p["Leerrohr"].paths.length > 0, "Leerrohr hat Pfade");
};

// Gegenprobe: ein Einzeltor hat keine Schiebetor-Fundamente/Leerrohr.
export const testEinzeltorHatKeineSchiebetorteile: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Strecke", [5]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Einzeltor_Easy"),
    schaffePlatzFallsDoppeltor: false,
  });
  agt.update3d();
  const tor = agt.getTore()[0];
  assert.isFalse(tor.isSchiebetor, "ist kein Schiebetor");
  const p = tor.schiebetorFixPunkt as any;
  assert.isTrue(!p?.["Schiebetor_Fundament1"], "Einzeltor darf kein Schiebetor_Fundament1 haben");
  assert.isTrue(!p?.["Leerrohr"], "Einzeltor darf kein Leerrohr haben");
};

// ---------------------------------------------------------------------------
// 5) Die Fundamente werden entlang Z gespiegelt, wenn sich die Schieberichtung ändert
//    (mirrorZ in updateSchiebetor hängt an der Schieberichtung)
// ---------------------------------------------------------------------------
export const testFundamenteWerdenGespiegelt: Test = async () => {
  const sumY = (tor: ZElement) =>
    ((tor.schiebetorFixPunkt as any)["Schiebetor_Fundament1"].points as Array<{ y: number }>).reduce(
      (acc, pt) => acc + pt.y,
      0
    );
  const links = await createSchiebetor("Schieberichtung_links", "Schiebetormotorseite_A");
  const rechts = await createSchiebetor("Schieberichtung_rechts", "Schiebetormotorseite_A");
  const sl = sumY(links.tor);
  const sr = sumY(rechts.tor);
  assert.isTrue(Math.abs(sl) > 0.0001, "Fundament-Punkte dürfen nicht degeneriert sein");
  // Z-Spiegelung: die Summe der gespiegelten Koordinate kehrt das Vorzeichen um.
  assert.equals(sl, -sr, "Fundament1 ist zwischen links/rechts entlang Z gespiegelt", 0.0001);
};
